import { useEffect, useMemo, useState } from "react";
import { useHistory, useParams } from "react-router-dom";
import {
  classificarFaixaEtaria,
  getCodigoPergunta,
  getOpcoesPsicossocial,
  VERSAO_PONTUACAO_PSICOSSOCIAL,
  DIMENSOES_PSICOSSOCIAIS,
} from "./psicossocialUtils";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Paper,
  Stack,
  Typography,
} from "@mui/material";
import {
  collection,
  doc,
  getDoc,
  getDocs,
  orderBy,
  query,
  runTransaction,
  serverTimestamp,
  where,
} from "firebase/firestore";
import { signOut } from "firebase/auth";

import { auth, db } from "../Config/firebase";
import {
  getCycleMeta,
  normalizePeriodicidade,
} from "../KPIs/ciclo.utils";

function normalizarIdentificador(valor = "") {
  return String(valor)
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9]/gi, "")
    .toLowerCase();
}

function ehKpiPsicossocial(kpi = {}) {
  const tipo = normalizarIdentificador(kpi.tipoFormulario);
  const nome = normalizarIdentificador(kpi.nome);
  const codigo = normalizarIdentificador(kpi.codigo);

  return (
    tipo === "psicossocial" ||
    nome.includes("psicossoc") ||
    codigo === "rq103"
  );
}

export default function ResponderPsicossocial() {
  const { token } = useParams();
  const history = useHistory();
  const [colaborador, setColaborador] = useState(null);
  const [colaboradorRefId, setColaboradorRefId] = useState("");
  const [kpi, setKpi] = useState(null);
  const [perguntas, setPerguntas] = useState([]);
  const [respostas, setRespostas] = useState({});
  const [loading, setLoading] = useState(true);
  const [enviando, setEnviando] = useState(false);
  const [erro, setErro] = useState("");
  const [sucesso, setSucesso] = useState(false);
  const [mostrarAvisoPerguntas, setMostrarAvisoPerguntas] = useState(false);
  const OPCOES_SEVERIDADE = [
  {
    valor: 1,
    titulo: "1. Insignificante",
    descricao:
      "Sem impacto relevante à saúde.",
  },
  {
    valor: 2,
    titulo: "2. Leve",
    descricao:
      "Desconforto temporário.",
  },
  {
    valor: 3,
    titulo: "3. Moderada",
    descricao:
      "Necessidade de acompanhamento ou intervenção.",
  },
  {
    valor: 4,
    titulo: "4. Grave",
    descricao:
      "Afastamento ou comprometimento significativo da saúde.",
  },
  {
    valor: 5,
    titulo: "5. Catastrófica",
    descricao:
      "Dano grave ou permanente à saúde.",
  },
];

const [severidades, setSeveridades] = useState({});


  useEffect(() => {
    carregar();
  }, [token]);

  async function carregar() {
    try {
      setLoading(true);
      setErro("");

      if (typeof auth.authStateReady === "function") {
        await auth.authStateReady();
      }

      if (auth.currentUser) {
        await signOut(auth);
      }

      const colaboradorDoc = await getDoc(doc(db, "psicossocial_colaboradores", token));

      if (!colaboradorDoc.exists()) {
        setErro("Link nao encontrado.");
        return;
      }

      const colaboradorData = { id: colaboradorDoc.id, ...colaboradorDoc.data() };

      const statusColaborador = String(colaboradorData.status || "").toLowerCase();
      const jaRespondido =
        statusColaborador === "respondido" ||
        Boolean(colaboradorData.respostaId);

      if (jaRespondido) {
        history.replace(`/psicossocial/resultados/${token}`);
        return;
      }

      if (colaboradorData.linkAtivo === false || statusColaborador !== "pendente") {
  setErro("Este link está inativo.");
  return;
}

      const kpiSnap = await getDoc(doc(db, "kpis", colaboradorData.kpiId));

      if (!kpiSnap.exists() || !ehKpiPsicossocial(kpiSnap.data())) {
        setErro("Pesquisa psicossocial nao encontrada.");
        return;
      }

      const perguntasSnap = await getDocs(
        query(
          collection(db, "kpi_perguntas"),
          where("kpiId", "==", colaboradorData.kpiId),
          where("ativa", "==", true),
          orderBy("ordem", "asc")
        )
      );

      setColaborador(colaboradorData);
      setColaboradorRefId(colaboradorDoc.id);
      setKpi({ id: kpiSnap.id, ...kpiSnap.data() });
      setPerguntas(perguntasSnap.docs.map((item) => ({ id: item.id, ...item.data() })));
    
      
      } catch (error) {
  console.error("ERRO PESQUISA", error);

  setErro(
    `${error.code || ""} - ${error.message || "Erro ao carregar pesquisa"}`
  );
}finally {
      setLoading(false);
    }
  }

const perguntasSemResposta = useMemo(() => {
  return perguntas.filter((pergunta) => {
    return (
      respostas[pergunta.id] === undefined ||
      severidades[pergunta.id] === undefined
    );
  });
}, [perguntas, respostas, severidades]);

function responder(id, valor) {
  setRespostas((prev) => ({
    ...prev,
    [id]: valor,
  }));

  setSeveridades((prev) => {
    const novo = { ...prev };

    if (Number(valor) === 0) {
      novo[id] = 1;
    } else {
      delete novo[id];
    }

    return novo;
  });
}

function responderSeveridade(id, valor) {
  setSeveridades((prev) => ({
    ...prev,
    [id]: valor,
  }));
}

  async function enviar() {
    setErro("");

    if (perguntasSemResposta.length > 0) {
  setMostrarAvisoPerguntas(true);
  return;
}

setMostrarAvisoPerguntas(false);

    let obtida = 0;
    let maxima = 0;
    const respostasPorCodigo = {};

    perguntas.forEach((pergunta) => {
      const opcoes = getOpcoesPsicossocial(pergunta);
      const valor = Number(respostas[pergunta.id]);
      const peso = Number(pergunta.peso || 1);
      const codigo = getCodigoPergunta(pergunta) || pergunta.id;

      const probabilidade = valor + 1;

        const severidade =
          Number(severidades[pergunta.id] || 1);

        const risco =
          probabilidade * severidade;

        respostasPorCodigo[codigo] = {
          valorResposta: valor,
          probabilidade,
          severidade,
          risco,
        };

      if (opcoes.length && Number.isFinite(valor)) {
        obtida += valor * peso;
        maxima += Math.max(...opcoes.map((opcao) => Number(opcao.valor || 0))) * peso;
      }
    });

    const indice = maxima ? Number(((obtida / maxima) * 100).toFixed(2)) : 0;
    const periodicidadeCiclos = normalizePeriodicidade(kpi?.periodicidadeCiclos);
    const cicloAtual = getCycleMeta(new Date(), periodicidadeCiclos);

    try {
      setEnviando(true);

      const colaboradorDocRef = doc(db, "psicossocial_colaboradores", colaboradorRefId);
      const respostaDocRef = doc(collection(db, "kpi_respostas"));

      await runTransaction(db, async (transaction) => {
        const colaboradorAtualSnap = await transaction.get(colaboradorDocRef);
        const colaboradorAtual = colaboradorAtualSnap.data();

        if (!colaboradorAtualSnap.exists() || !colaboradorAtual?.linkAtivo || colaboradorAtual?.status !== "pendente") {
          throw new Error("Este link ja foi utilizado ou esta inativo.");
        }

        transaction.set(respostaDocRef, {
          kpiId: kpi.id,
          kpiNome: kpi.nome || "KPI Psicossocial",
          tenantId:
            colaboradorAtual.tenantId ||
            colaborador.tenantId ||
            kpi.tenantId ||
            "",
          tipoFormulario: "PSICOSSOCIAL",
          origem: "link_psicossocial",
          anonima: true,
          visivelResultadoPublico: true,
          tipoUsuario: "colaborador",
          psicossocialColaboradorId: colaboradorRefId,
          faixaEtaria: classificarFaixaEtaria(colaborador.dataNascimento),
          sexo: colaborador.sexo || "Não informado",
          setor: colaborador.setor || "",
          funcao: colaborador.funcao || "",
          clienteId: colaborador.clienteId || "",
          clienteNome: colaborador.clienteNome || "",
          respostas: respostasPorCodigo,
          respostasPorPergunta: respostas,
          versaoPontuacaoPsicossocial: VERSAO_PONTUACAO_PSICOSSOCIAL,
          pontuacaoObtida: obtida,
          pontuacaoMaxima: maxima,
          indice,
          ciclo: cicloAtual.chave,
          cicloLabel: cicloAtual.rotulo,
          ano: cicloAtual.ano,
          cicloNumero: cicloAtual.cicloNumero,
          trimestre: periodicidadeCiclos === 4 ? cicloAtual.cicloNumero : null,
          periodicidadeCiclos,
          createdAt: serverTimestamp(),
        });

        transaction.update(colaboradorDocRef, {
          status: "respondido",
          linkAtivo: false,
          respostaId: respostaDocRef.id,
          respondidoEm: serverTimestamp(),
        });
      });

      setSucesso(true);
      window.setTimeout(() => {
        history.replace(`/psicossocial/resultados/${token}`);
      }, 4500);
    } catch (error) {
      console.error(error);
      setErro(error.message || "Erro ao enviar respostas.");
    } finally {
      setEnviando(false);
    }
  }

  if (loading) {
    return (
      <Box sx={{ minHeight: "100vh", display: "grid", placeItems: "center" }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ minHeight: "100vh", backgroundColor: "#f4f6f8", p: { xs: 2, md: 4 } }}>
      <Paper sx={{ maxWidth: 980, mx: "auto", p: { xs: 2, md: 4 } }}>
        <Typography variant="h5" fontWeight={700} mb={1}>
          {kpi?.nome || "Pesquisa Psicossocial"}
        </Typography>
        <Typography color="text.secondary" mb={3}>
          Responda com tranquilidade. O link e individual e sera invalidado apos o envio.
        </Typography>

        {erro && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {erro}
          </Alert>
        )}

        {sucesso && (
          <Alert severity="success" sx={{ mb: 2 }}>
            Obrigado por contribuir com a pesquisa. O resultado da pesquisa e anônimo.
            Todos os funcionários que contribuirem para a pesquisa poderão ver os resultados globais.
          </Alert>
        )}

       {!sucesso && !erro && (
          <>
            <Stack spacing={3}>
              {perguntas.map((pergunta, index) => {
                const opcoes = getOpcoesPsicossocial(pergunta);
                const codigo = getCodigoPergunta(pergunta);

                return (
                  <Box key={pergunta.id}>
                    <Typography fontWeight={700} mb={1}>
                      {index + 1}. {pergunta.texto}
                    </Typography>

                    <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: `repeat(${Math.max(opcoes.length, 1)}, 1fr)` }, border: "1px solid #d0d7de", borderRadius: 1, overflow: "hidden" }}>
                      {opcoes.map((opcao, opcaoIndex) => {
                        const ativo = respostas[pergunta.id] === opcao.valor;

                        return (
                          <Button
                            key={`${codigo}-${opcaoIndex}`}
                            onClick={() => responder(pergunta.id, opcao.valor)}
                            sx={{
                              minHeight: 54,
                              borderRadius: 0,
                              borderRight: { md: opcaoIndex === opcoes.length - 1 ? 0 : "1px solid #d0d7de" },
                              borderBottom: { xs: opcaoIndex === opcoes.length - 1 ? 0 : "1px solid #d0d7de", md: 0 },
                              backgroundColor: ativo ? "#e7f1ff" : "#fff",
                              color: "text.primary",
                            }}
                          >
                            {opcao.label} ({opcao.valor})
                          </Button>
                        );
                      })}

                      {respostas[pergunta.id] !== undefined &&
                        Number(respostas[pergunta.id]) !== 0 && (
  <Box sx={{ mt: 1, gridColumn: "1 / -1" }}>
    <Typography
      fontWeight={600}
      mb={1}
      color="blue"
    >
      
        .  Isso impactaria como?
    </Typography>

    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: {
  xs: "1fr",
  md: "1fr",
},
        border: "1px solid #d0d7de",
        borderRadius: 1,
        overflow: "hidden",
      }}
    >
      {OPCOES_SEVERIDADE.map((opcao, idx) => {
        const ativo =
          severidades[pergunta.id] === opcao.valor;

        return (
          <Button
  key={idx}
  onClick={() =>
    responderSeveridade(
      pergunta.id,
      opcao.valor
    )
  }
  sx={{
    minHeight: "auto",
    borderRadius: 0,
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-start",
    justifyContent: "flex-start",
    gap: 2,
    textAlign: "left",
    padding: "8px 16px",
    backgroundColor: ativo
      ? "#ffe6e6"
      : "#fff",
  }}
>
  <Typography
  sx={{
    fontWeight: 700,
    fontSize: 15,
    lineHeight: 1,
  }}
>
  {opcao.titulo}
</Typography>

  <Typography
  variant="caption"
  sx={{
    mt: 0,
    textAlign: "left",
    lineHeight: 1.1,
      whiteSpace: "normal",
      wordBreak: "normal",
      overflowWrap: "break-word",
    }}
  >
    {opcao.descricao}
  </Typography>
</Button>
        );
      })}
    </Box>
  </Box>
)}
                    </Box>
                  </Box>
                );
              })}
            </Stack>

            <Box
              sx={{
                display: "flex",
                justifyContent: "flex-end",
                alignItems: "center",
                gap: 2,
                mt: 4,
              }}
            >
              {mostrarAvisoPerguntas && (
  <Typography
    sx={{
      color: "#d32f2f",
      fontWeight: 600,
      fontSize: 14,
    }}
  >
    Para finalizar, responda todas as perguntas.
  </Typography>
)}

              <Button
                variant="contained"
                color="success"
                disabled={enviando}
                onClick={enviar}
              >
                {enviando && (
                  <CircularProgress
                    size={18}
                    color="inherit"
                    sx={{ mr: 1 }}
                  />
                )}

                {enviando ? "Enviando..." : "Enviar questionario"}
              </Button>
            </Box>
          </>
        )}
      </Paper>
    </Box>
  );
}
